// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Chargement de Stripe.js, à la demande.
//
// Sert UNIQUEMENT à l'authentification 3-D Secure d'un changement de forfait :
// Stripe recommande de récupérer le `client_secret` du paiement et de le passer
// à `handleNextAction`, qui « guide le client à travers les étapes nécessaires
// et renvoie le résultat à votre application ».
//
// ⚠️ POURQUOI PAS LA PAGE DE FACTURE HÉBERGÉE : elle n'offre AUCUN retour. Le
// client qui renonçait devait fermer son onglet, et le changement restait en
// attente jusqu'à expiration (23 h). Ici l'authentification se fait en surcouche
// de Normascan : fermer la fenêtre revient à renoncer, sans rien laisser
// derrière soi.
//
// ⚠️ Chargé À LA DEMANDE et non au démarrage : c'est un script tiers de ~200 Ko
// qu'aucune autre page n'utilise. Le charger sur tout le site le ferait payer à
// chaque visiteur, pour une fonction que presque personne n'atteint.

const URL_STRIPE_JS = 'https://js.stripe.com/v3/';

let chargement = null;

/** Charge Stripe.js une seule fois, puis rend l'instance prête à l'emploi. */
export const chargerStripe = (clePublique) => {
  if (!clePublique) return Promise.resolve(null);

  if (!chargement) {
    chargement = new Promise((resoudre, rejeter) => {
      if (window.Stripe) { resoudre(window.Stripe); return; }

      // Un script déjà posé par un chargement précédent (navigation interne)
      // ne doit pas être ajouté deux fois.
      const existant = document.querySelector(`script[src="${URL_STRIPE_JS}"]`);
      if (existant) {
        existant.addEventListener('load', () => resoudre(window.Stripe));
        existant.addEventListener('error', rejeter);
        return;
      }

      const script = document.createElement('script');
      script.src = URL_STRIPE_JS;
      script.async = true;
      script.onload = () => resoudre(window.Stripe);
      script.onerror = () => rejeter(new Error('Stripe.js inaccessible'));
      document.head.appendChild(script);
    });
  }

  return chargement.then((Stripe) => (Stripe ? Stripe(clePublique) : null));
};

/**
 * Fait authentifier un paiement DANS l'application.
 *
 * Rend `{ ok: true }` si le paiement a abouti, `{ ok: false, annule: true }` si
 * le client a renoncé, `{ ok: false, erreur }` sinon.
 *
 * ⚠️ Distinguer « renoncé » de « échoué » est essentiel : dans le premier cas
 * on propose d'abandonner le changement, dans le second on affiche une erreur.
 */
export const authentifierPaiement = async (clePublique, clientSecret) => {
  try {
    const stripe = await chargerStripe(clePublique);
    if (!stripe) return { ok: false, erreur: 'Stripe indisponible' };

    const { error, paymentIntent } = await stripe.handleNextAction({ clientSecret });

    if (error) {
      // Stripe ne dit pas explicitement « l'utilisateur a fermé la fenêtre » :
      // il rend une erreur de type `validation_error` ou un intent resté en
      // `requires_action`. Les deux signifient qu'il n'a pas confirmé.
      const aRenonce = error.type === 'validation_error'
        || error.code === 'payment_intent_authentication_failure';
      return { ok: false, annule: aRenonce, erreur: error.message };
    }

    if (paymentIntent && paymentIntent.status === 'succeeded') {
      return { ok: true };
    }
    // `requires_action` encore présent = fenêtre fermée sans confirmer.
    return { ok: false, annule: true };
  } catch (e) {
    return { ok: false, erreur: e.message };
  }
};
